/**
 * @ngdoc object
 * @name window.tbg.productConfigService
 * @description
 * `window.tbg.productConfigService`
 *
 * Storefront product config service, provides various product config related functionality. This code is currently only available on the storefront product page, so make sure to test
 * for it before using if there is the potential for it to be called outside of that context: `if (window.tbg && window.tbg.productConfigService) { ... }`
 *
 * Implementation note: the window.tbg.productConfigService.getShareableLink() method was built with the intention that the theme team could implement a shared config link button without
 * being limited to placement. In the event that javascript is used to shim a button somewhere within the markup generated by the `[[ product.configure ]]` theme tag, the placement of said button
 * should wait for the `onCalcLoaded` event to ensure the markup has rendered. A re-render also has the potential to wipe out any buttons placed, depending on where the button is placed, so we may
 * also want to reset for every `onQuoteUpdated` event. Any time the quote is updated, it will also invalidate any previously retreived urls, so it is up to the theme to determine how to handle that.
 */

/**
 * @ngdoc method
 * @name window.tbg.productConfigService#supportsShareableLink
 * @methodOf window.tbg.productConfigService
 * @description Whether or not `window.tbg.productConfigService.getShareableLink()` can be called for this product. For this method to work properly, it must be called after the `onCalcLoaded` event has fired.
 *
 * @returns {boolean} Returns `true` if `window.tbg.productConfigService.getShareableLink()` can be called for this product
 * @example
 <pre>
    // For example, maybe we only want to show the get link button if supported for this product
    configureEvents.registerOnCalcLoadedListener({
        onCalcLoaded: function () {
            if (window.tbg && window.tbg.productConfigService && window.tbg.productConfigService.supportsShareableLink()) {
                showButton();
            } else {
                hideButton();
            }
        }
    });
 </pre>
 */

/**
 * @ngdoc method
 * @name window.tbg.productConfigService#getShareableLink
 * @methodOf window.tbg.productConfigService
 * @description Async method that fetches a unique url representing the current product configuration and returns it either via a resolved jQuery.Deferred object or via the callback arguments provided.
 *
 * @returns {Deferred} Returns a jQuery Deferred object
 * @param { function (shareableLink) =} successCallback Function to call when request to get link is successful.
 * ###Arguments:
 * `shareableLink` (string) - the url of the shareable product configuration.
 * @param { function (jqXHR, status, error) =} errorCallback Function to call when request to get link fails.
 * ###Arguments:
 * `jqXHR` (jqXHR) - the jQuery XMLHttpRequest object used in making the request. This param will be `null` if the call is aborted before the request is initialized.
 *
 * `status` (string) - A string describing the type of error that occurred. Possible values are `null`, `"timeout"`, `"error"`, `"abort"`, `"early-abort"` and `"parsererror"`. If the call is aborted before the request is initialized, this value will be `"early-abort"`.
 *
 * `error` (string) - A string containing more details about the reason this call has errored out.
 *
 * @param { function (jqXHR, status) =} completeCallback Function to call when request either completes successfully or fails. Called after the success and error callbacks have been called. Useful for when you need to do some work when this request is complete, regardless of success or failure.
 * ###Arguments:
 * `jqXHR` (jqXHR) - the jQuery XMLHttpRequest object used in making the request. This param will be `null` if the call is aborted before the request is initialized.
 *
 * `status` (string) - A string describing the status of the request. Possible values are `"success"`, `"notmodified"`, `"nocontent"`, `"error"`, `"timeout"`, `"abort"`, `"early-abort"`, or `"parsererror"`. If the call is aborted before the request is initialized, this value will be `"early-abort"`.
 *
 * @example
 <pre>
    // Example using callbacks
    function getLink () {
        showLoadingIndicator(); // Let's say we want to show a loading indicator while the request is pending...

        return window.tbg.productConfigService.getShareableLink(successCb, errorCb, completeCb); // Make the request, passing in our callbacks

        // Define our callbacks...
        function successCb (shareLink) {
            // Success! Show the link to the users.
            alert('Share your product configuration with your friends! ' + shareLink);
        }

        function errorCb (xhr, status, error) {
            // Failure! Handle the error somehow...
            alert('Could not complete your request! ' + error);
        }

        function completeCb (xhr, status) {
            // Success or fail, we are now complete, let's clean up after ourselves...
            hideLoadingIndicator(); // Regardless of error or success, the request is complete so let's hide the loading indicator
        }
    }

    // Example using the Deferred
    function deferredGetLink () {
        showLoadingIndicator(); // Let's say we want to show a loading indicator while the request is pending...

        // Call getShareableLink chaining jQuery deferred methods then/fail/always to handle the outcome
        return window.tbg.productConfigService.getShareableLink()
            .then(onSuccess)
            .fail(onError)
            .always(onComplete);

        // Define our deferred handlers
        function onSuccess (shareLink, status, xhr) {
            // Success! Show the link to the users.
            alert('Share your product configuration with your friends! ' + shareLink);
        }

        function onError (xhr, status, error) {
            // Failure! Handle the error somehow...
            alert('Could not complete your request! ' + error);
        }

        function onComplete () {
            // Success or fail, we are now complete, let's clean up after ourselves...
            hideLoadingIndicator(); // Regardless of error or success, the request is complete so let's hide the loading indicator
        }
    }

    // Full example implementation
    (function setUpShareableLinkFeature () {
        if (!window.tbg || !window.tbg.productConfigService) {
            return;
        }

        var $btn;
        var $calcDiv;
        var $linkDiv;

        configureEvents.registerOnCalcLoadedListener({ onCalcLoaded: init });
        configureEvents.registerOnQuoteUpdatedListener({ onQuoteUpdated: render });

        return;

        function init () {
            if (!window.tbg.productConfigService.supportsShareableLink()) {
                return;
            }

            $calcDiv = $(document.getElementById('calculator'));
            $linkDiv = $('<div></div>');
            $btn = $('<button>Get Link</button>');
            $btn.on('click', handleButtonClick);

            render();
        }

        function render () {
            $linkDiv.text('');
            $calcDiv.append($linkDiv, $btn);
        }

        function handleButtonClick () {
            if ($btn.hasClass('loading')) {
                return;
            }

            $btn.addClass('loading');

            getShareableLink().always(function(){
                $btn.removeClass('loading');
            });
        }

        function getShareableLink () {
            return window.tbg.productConfigService.getShareableLink(successCb, errorCb);

            function successCb (link) {
                $linkDiv.text('Use link ' + link + ' to access this configuration for this product in the future. Please note that any further changes to product configuration will require fetching a new configuration link to ensure your changes are saved.');
            }

            function errorCb () {
                $linkDiv.text('');
                alert('Could not get link, please try again.');
            }
        }
    }())
 </pre>
 */

(function (window, $) {
    'use strict';

    if (!$) {
        console.error('window.tbg.productConfigService requires jQuery to be loaded first.');
        return;
    }

    if (window.tbg && window.tbg.productConfigService) {
        console.warn('window.tbg.productConfigService has already been loaded.');
        return;
    }

    var service = {
        supportsShareableLink: supportsShareableLink,
        getShareableLink: getShareableLink
    };

    window.tbg = window.tbg || {};
    window.tbg.productConfigService = service;

    return;

    function supportsShareableLink () {
        return window.configureglobals && window.configureglobals.coffering && (window.configureglobals.coffering.isLargeFormatPOD() || window.configureglobals.coffering.isSmallFormatPOD());
    }

    function getShareableLink (successCb, errorCb, completeCb) {
        successCb = selfOrNoop(successCb);
        errorCb = selfOrNoop(errorCb);
        completeCb = selfOrNoop(completeCb);

        if (!window.configureglobals || !window.configureglobals.coffering) {
            return warnAndAbort('Cannot use window.tbg.productConfigService.getShareableLink() on non-product page.');
        }

        if (!supportsShareableLink()) {
            return warnAndAbort('Calling window.tbg.productConfigService.getShareableLink() on a stock item is not supported.');
        }

        return $.ajax({
            url: '/rest/v1/storefront/products/' + window.configureglobals.coffering.id + '/share',
            method: 'POST',
            contentType: 'application/json',
            success: function onGetShareableLinkSuccess (response, status, xhr) {
                successCb(response);
            },
            error: errorCb,
            complete: completeCb,
            data: generateSmallOrLargeConfiguration()
        });

        function generateSmallOrLargeConfiguration () {
            /* eslint-disable function-paren-newline */
            return JSON.stringify(
                window.configureglobals.coffering.isSmallFormatPOD()
                    ? generateConfiguration(smallFormatMap(), 'calculator', '.configSelect, .configField, .opSelect')
                    : generateConfiguration(largeFormatMap(), 'lfCalculator', '.configSelect, .configField, .opSelect, .configCheckbox-input')
            );
            /* eslint-enable function-paren-newline */

            function generateConfiguration (nameMap, calcId, inputSelectors) {
                var config = {};
                var $inputs = $(document.getElementById(calcId)).find(inputSelectors);

                $inputs.each(function () {
                    var $this = $(this);
                    var name = $this.prop('name');
                    var operationId = name.substring(0, 'OPERATION'.length) === 'OPERATION' ? this.id.match(/\d+/)[0] : '';

                    for (var prop in nameMap) {
                        if (nameMap.hasOwnProperty(prop) && name.replace(/[0-9]+$/g, '') === prop) {
                            name = name.replace(prop, nameMap[prop]);
                            name = operationId ? name.replace(/\d+/, operationId) : name;
                            config[name] = $this.is('[type="checkbox"]') ? this.checked : $this.val();
                            break;
                        }
                    }
                });

                return config;
            }

            function smallFormatMap () {
                return {
                    DEVICEDD: 'deviceType',
                    PRESSSHEETTYPEDD: 'pressSheetType',
                    PRESSSHEETWEIGHTDD: 'pressSheetWeight',
                    PRESSSHEETCOLORDD: 'pressSheetColor',
                    COVERSIDE1DD: 'coverSide1InkType',
                    COVERSIDE2DD: 'coverSide2InkType',
                    SIDE1DD: 'side1InkType',
                    SIDE2DD: 'side2InkType',
                    COVERPRESSSHEETTYPEDD: 'coverPressSheetType',
                    COVERPRESSSHEETWEIGHTDD: 'coverPressSheetWeight',
                    COVERPRESSSHEETCOLORDD: 'coverPressSheetColor',
                    VERSION_COUNT_EF: 'versions',
                    QUANTITYEF: 'qty',
                    QUANTITY_PER_VERSION_EF: 'qtyPerVersion',
                    VERSIONNAMEEF: 'versionName_',
                    VERSION_QUANTITY_EF: 'versionQty_',
                    SKU_EF: 'versionSku_',
                    PAGESEF: 'pages',
                    PRESETDIMENSIONSDD: 'presetDimensions',
                    WIDTHEF: 'width',
                    HEIGHTEF: 'height',
                    BLEEDDD: 'bleed',
                    PROOFDD: 'proof',
                    TURNAROUNDTIMEDD: 'turnaroundTime',
                    OPERATIONDD: 'operation_',
                    OPERATIONITEMANSWEREF: 'operationQuestionAnswer_'
                };
            }

            function largeFormatMap () {
                return {
                    PRINTSUBSTRATEDD: 'printSubstrate',
                    MOUNTSUBSTRATEDD: 'mountSubstrate',
                    FRONTLAMINATEDD: 'frontLaminate',
                    BACKLAMINATEDD: 'backLaminate',
                    QUANTITYEF: 'qty',
                    PIECE_MARGIN_TOP_EF: 'pieceMarginTop',
                    PIECE_MARGIN_BOTTOM_EF: 'pieceMarginBottom',
                    PIECE_MARGIN_LEFT_EF: 'pieceMarginLeft',
                    PIECE_MARGIN_RIGHT_EF: 'pieceMarginRight',
                    PIECE_BLEED_TOP_EF: 'pieceBleedTop',
                    PIECE_BLEED_BOTTOM_EF: 'pieceBleedBottom',
                    PIECE_BLEED_LEFT_EF: 'pieceBleedLeft',
                    PIECE_BLEED_RIGHT_EF: 'pieceBleedRight',
                    INSTALLER_BLEED_TOP_EF: 'installerBleedTop',
                    INSTALLER_BLEED_BOTTOM_EF: 'installerBleedBottom',
                    INSTALLER_BLEED_LEFT_EF: 'installerBleedLeft',
                    INSTALLER_BLEED_RIGHT_EF: 'installerBleedRight',
                    SIDESDD: 'sides',
                    PRESETDIMENSIONSDD: 'presetDimensions',
                    WIDTHEF: 'width',
                    HEIGHTEF: 'height',
                    VERSION_COUNT_EF: 'versions',
                    QUANTITY_PER_VERSION_EF: 'qtyPerVersion',
                    VERSIONNAMEEF: 'versionName_',
                    VERSION_QUANTITY_EF: 'versionQty_',
                    SKU_EF: 'versionSku_',
                    BLEEDDD: 'bleed',
                    PROOFDD: 'proof',
                    OPERATIONDD: 'operation_',
                    OPERATIONQUANTITYEF: 'operationQty_',
                    OPERATION_ADD_ON_VALUE_EF: 'operationAddOnValue_',
                    OPERATIONDD_CB_TOP_: 'operationSideTop_',
                    OPERATIONDD_CB_BOTTOM_: 'operationSideBottom_',
                    OPERATIONDD_CB_LEFT_: 'operationSideLeft_',
                    OPERATIONDD_CB_RIGHT_: 'operationSideRight_'
                };
            }
        }

        function warnAndAbort (msg) {
            console.warn(msg);
            errorCb(null, 'early-abort', msg);
            completeCb(null, 'early-abort');
            return $.Deferred().reject(null, 'early-abort', msg);
        }

        function selfOrNoop (func) {
            return func && func.constructor === Function ? func : function noop () { /* noop */ };
        }
    }
}(window, window.jQuery));
